import { describeRelativeTime } from "@nova/format"
import {
    ExtraSmallIcon,
    Folder as FolderIcon,
    Map,
    SmallIcon,
    Star,
    StarFill,
    ThreeDotsSettings,
} from "@nova/component-icons"
import { useTranslation } from "@nova/use-translation"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import classNames from "classnames"
import { animationClass, defaultFolderName, WorkspaceTreeMaxDepth } from "../../../../constants"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ContextMenu } from "../../../../TODO-put-in-package/ContextMenu"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { createContextMenuSections } from "features/data-management/workspace/module"
import { selectIdRenaming } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useWorkspaceFolderDragAndDrop } from "../../../useWorkspaceFolderDragAndDrop"
import { WorkspaceEntityType, WorkspaceFolderProps } from "features/data-management/workspace/types"
import { FolderSVG } from "./FolderSVG"
import { WorkspaceCardViewers } from "./WorkspaceCardViewers"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { useRouter } from "pages"

export const WorkspaceFolderCard = ({
    folder,
    tagChain,
    numberOfSubFolders,
    numberOfSubProjects,
    mostRecentChildUpdatedAt,
    isFavorite,
    viewers,
    drones,
    handleMove,
    handleCreateFolder,
    handleBeginRenaming,
    handleConfirmRenaming,
    handleCancelRenaming,
    handleOpenShareModal,
    handleToggleFavorite,
    handleDelete,
}: WorkspaceFolderProps) => {
    // Need this since multiple entities with the same ID can exist on the screen in parallel.
    const key = `grid_${folder.id}`
    const type = WorkspaceEntityType.Folder

    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isHovering, setIsHovering] = useState(false)
    const { isEditor } = useWorkspacePermissions(folder.id)
    const activeGridFolderContextMenu = useAppSelector(selectActiveContextMenu("GridFolder"))
    const contextMenuIsForThisCard = activeGridFolderContextMenu?.data.folderId === folder.id
    const renameRef = useRef<HTMLInputElement>(null)
    const idRenaming = useAppSelector(selectIdRenaming())
    const isRenaming = idRenaming === key
    const canCreateFolder = tagChain.length <= WorkspaceTreeMaxDepth

    const contextMenuSections = useMemo(
        () =>
            createContextMenuSections({
                id: folder.id,
                key,
                title: folder.name,
                isFavorite,
                onCreateFolder: isEditor && canCreateFolder ? handleCreateFolder : undefined,
                onToggleFavorite: handleToggleFavorite,
                onBeginRenaming: isEditor ? handleBeginRenaming : undefined,
                // onMoveTo: ,
                onOpenShareModal: handleOpenShareModal,
                onDelete: isEditor ? (id, name) => handleDelete(id, name, type) : undefined,
            }),
        [
            folder,
            key,
            type,
            isFavorite,
            isEditor,
            canCreateFolder,
            handleCreateFolder,
            handleToggleFavorite,
            handleBeginRenaming,
            handleOpenShareModal,
            handleDelete,
        ]
    )

    const { dragAndDropRef, isOver, canDrop, isDragging, anythingIsDragging } =
        useWorkspaceFolderDragAndDrop({
            id: folder.id,
            subFolders: folder.subTags,
            isRenaming,
            isEditor,
            handleMove,
        })

    // Cancel renaming on clickaway.
    const handleClickAway = () => {
        if (idRenaming) handleCancelRenaming?.()
    }

    // Focus the text field when renaming begins.
    useEffect(() => {
        if (isRenaming) renameRef.current?.focus()
    }, [isRenaming])

    /**
     * @param force
     * If `true`, function will force-show the context menu.
     * If `false`, function will force-hide the context menu.
     * If `undefined`, function will toggle the context menu.
     */
    const handleToggleContextMenu = (force?: boolean) => {
        const shouldHide = (contextMenuIsForThisCard && !force) || force === false
        if (force === false) setIsHovering(false)
        dispatch(
            setActiveContextMenu(
                shouldHide
                    ? undefined
                    : {
                          type: "GridFolder",
                          data: { folderId: folder.id, fixedPosition: false },
                          position: {
                              top: 0,
                              left: 0,
                          },
                      }
            )
        )
    }

    const router = useRouter("/workspace/:workspaceId")
    const handleOpenFolder = useCallback(() => {
        router.navigate({ path: "/explore/ws/*", splat: tagChain.join("/") })
    }, [router, tagChain])

    const shouldShow3DotsButton =
        (isHovering || contextMenuIsForThisCard) && contextMenuSections.length > 0 && !isRenaming

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div
                ref={dragAndDropRef}
                className={classNames("relative flex-1 w-full max-w-[20rem] h-[9rem]", {
                    "cursor-pointer": !isRenaming,
                    "opacity-20": (!canDrop && anythingIsDragging) || isDragging,
                })}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={!isRenaming ? () => handleOpenFolder() : (e) => e.stopPropagation()}
                onContextMenu={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    dispatch(
                        setActiveContextMenu({
                            type: "GridFolder",
                            data: {
                                folderId: folder.id,
                                fixedPosition: true,
                            },
                            position: { top: e.clientY, left: e.clientX },
                        })
                    )
                }}
            >
                <FolderSVG
                    fillColor={isRenaming ? "#0C0C0F" : "#28282C"} // gray-900, gray-700
                    borderColor={
                        isOver
                            ? canDrop
                                ? "#27BBFF"
                                : "#FA4F25"
                            : isRenaming
                              ? "#FFFFFF1A"
                              : isHovering
                                ? "#FFFFFF66"
                                : "transparent"
                    } // blue-500, red-500, white-200, white-400
                />
                <div className="absolute inset-x-[6.4%] top-[23.5%] bottom-[12%] flex flex-col gap-1 text-gray-100">
                    {/* Title */}
                    <div className="flex items-center gap-1">
                        <RenamableText
                            ref={renameRef}
                            Tag="h4"
                            title={folder.name}
                            id={folder.id}
                            isRenaming={isRenaming}
                            initialTitleIsPlaceholder={folder.name === defaultFolderName}
                            className={classNames("!h-6", {
                                "text-gray-0": isHovering,
                                "text-gray-50": !isHovering,
                                "!w-auto": !isRenaming,
                            })}
                            underlineClassNames={(isRenaming) =>
                                classNames({
                                    "border-blue-500": isRenaming,
                                    "border-transparent": !isRenaming,
                                })
                            }
                            onConfirmRenaming={(id, name) => handleConfirmRenaming(id, name, type)}
                            onCancelRenaming={handleCancelRenaming}
                        />
                        {isFavorite && !isRenaming && (
                            <SmallIcon Icon={StarFill} className="text-gray-0" />
                        )}
                    </div>

                    <WorkspaceCardViewers
                        viewers={viewers}
                        drones={drones}
                        className="-top-[1.9375rem] -right-3"
                    />

                    {/* Date Last Updated */}
                    <h5 className="select-none">
                        Updated:{" "}
                        {mostRecentChildUpdatedAt
                            ? describeRelativeTime(mostRecentChildUpdatedAt)
                            : "Now"}
                    </h5>

                    {/* Spacer */}
                    <div className="flex-grow"></div>

                    {/* Folder Stats */}
                    <div className="flex items-center gap-4 select-none h-5">
                        <div className="flex items-center gap-0.5">
                            <ExtraSmallIcon Icon={FolderIcon} />
                            <small>{t(`${numberOfSubFolders}`)}</small>
                        </div>
                        <div className="flex items-center gap-0.5">
                            <ExtraSmallIcon Icon={Map} />
                            <small>{t(`${numberOfSubProjects}`)}</small>
                        </div>
                        {/* <div className="flex items-center gap-0.5">
                            <ExtraSmallIcon Icon={People} />
                            <small>{t("TODO")}</small>
                        </div> */}

                        <div className="flex-grow"></div>

                        {/* Bottom-Right Buttons */}
                        <div
                            className={classNames(
                                "relative flex items-center gap-1 justify-end",
                                animationClass
                            )}
                        >
                            {/* Add to / Remove from Favorites */}
                            {isHovering && (
                                <SmallIcon
                                    Icon={!isFavorite ? Star : StarFill}
                                    className={classNames("cursor-pointer", animationClass, {
                                        "text-gray-300 hover:text-gray-0": !isFavorite,
                                        "text-yellow-500 hover:text-yellow-200": isFavorite,
                                    })}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleToggleFavorite(folder.id)
                                    }}
                                />
                            )}
                            {/* 3 Dots Button */}
                            <SmallIcon
                                Icon={ThreeDotsSettings}
                                className={classNames(
                                    "text-gray-300 hover:text-gray-0 cursor-pointer",
                                    animationClass,
                                    {
                                        "opacity-0": !shouldShow3DotsButton,
                                    }
                                )}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleToggleContextMenu()
                                }}
                            />

                            {/* Context Menu */}
                            {contextMenuIsForThisCard && (
                                <ContextMenu
                                    sections={contextMenuSections}
                                    onBlur={() => handleToggleContextMenu(false)}
                                    className="flex-none"
                                    isFixedPosition={activeGridFolderContextMenu.data.fixedPosition}
                                    style={
                                        !activeGridFolderContextMenu.data.fixedPosition
                                            ? { top: "1.5rem", right: "0.25rem" }
                                            : undefined
                                    }
                                    top={activeGridFolderContextMenu.position.top}
                                    left={activeGridFolderContextMenu.position.left}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </ClickAwayListener>
    )
}
